import { useCallback, useRef } from 'react';
import type { ViewStyle } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import type { GalleryProps, GalleryRefType } from 'react-native-zoom-toolkit';
import { Gallery } from 'react-native-zoom-toolkit';

import { CarouselWrapper } from '@/shared/components/carousel/CarouselWrapper';
import { DotPagination } from '@/shared/components/carousel/DotPagination';
import { useCarouselLayout } from '@/shared/hooks/useCarouselLayout';
import type { CarouselLayoutSize } from '@/shared/types/carousel';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props<T> = {
  renderItem: (
    item: T,
    layout: CarouselLayoutSize,
    index?: number
  ) => React.ReactElement;
  style?: ViewStyle;
} & Omit<GalleryProps<T>, 'renderItem'>;

export const ZoomCarousel = <T,>({
  renderItem,
  style,
  ...galleryProps
}: Props<T>) => {
  const { colors } = useAppTheme();

  const { layout, onLayout } = useCarouselLayout();

  const progress = useSharedValue<number>(0);

  const ref = useRef<GalleryRefType>(null);

  const handlePaginationPress = (index: number) => ref.current?.setIndex(index);

  const handleScroll = useCallback(
    (scroll: number) => {
      'worklet';
      progress.set(scroll / layout.width);
    },
    [progress, layout.width]
  );

  const handleRenderItem = useCallback(
    (item: T, index: number) => renderItem(item, layout, index),
    [layout, renderItem]
  );

  return (
    <>
      <CarouselWrapper
        style={style}
        onLayout={onLayout}
        progress={progress}
        size={galleryProps.data.length}
        onPressLeft={handlePaginationPress}
        onPressRight={handlePaginationPress}
      >
        {layout.width > 0 && (
          <Gallery<T>
            {...galleryProps}
            renderItem={handleRenderItem}
            ref={ref}
            onScroll={handleScroll}
          />
        )}
      </CarouselWrapper>

      <DotPagination
        color={colors.primary}
        progress={progress}
        size={galleryProps.data.length}
        onPress={handlePaginationPress}
      />
    </>
  );
};
